import { API_URLS } from '../config/api';
import { QueryResultSections, TokenUsage, ChatMessageRequest, Area } from '../types/project';
import { fileRegistryService } from './fileRegistry';
import { BackendErrorParser } from './errorParser';
import { authToken } from './authToken';

// EventSource cannot send the Authorization header the fetch interceptor
// injects, so the query stream URL carries the JWT via ?access_token=.
const openQueryStream = (queryId: string): EventSource =>
  new EventSource(API_URLS.getQueryStreamUrl(queryId, authToken.get() ?? ''));

export interface AgentStatus {
  agentId: string;
  agentName: string;
  status: 'idle' | 'processing' | 'completed' | 'failed';
  currentStep?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface ExecutionPlanAgent {
  agentId: string;
  agentName: string;
  description: string;
  order: number;
}

export interface ExecutionPlan {
  summary: string;
  agents: ExecutionPlanAgent[];
  estimatedTime?: string;
  createdAt?: string;
}

export interface StreamingQueryResponse {
  query_id?: string;
  queryId?: string;
  query?: string;
  status: 'processing' | 'completed' | 'failed' | 'pending_approval' | 'rejected';
  progress?: number;
  progressPercentage?: number;
  steps?: QueryStep[];
  result?: any;  // For research mode
  final_response?: string;  // For chat mode - the actual answer
  mode?: 'chat' | 'research';  // Mode indicator
  has_sources?: boolean;  // Whether sources are available
  structured_response?: QueryResultSections;
  areas?: Record<string, Area>;
  done?: boolean;
  executionPlan?: ExecutionPlan;
  currentStep?: string;
  currentAgent?: string;
  activeAgent?: string;
  engagedAgents?: string[];
  completedAgents?: string[];
  agentStatuses?: AgentStatus[];
  totalSteps?: number | null;
  startTime?: string;
  endTime?: string;
  timestamp?: string;
  context?: any;
  visualizations?: any[];
  sources?: any[];  // Source citations for both modes
  confidence_score?: number | null;
  execution_time_ms?: number | null;
  errors?: any[];
  rejection_reason?: string;
  error?: string;
  metadata?: any;  // Additional metadata
}

export interface QueryStep {
  stepId: string;
  agent: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  action: string;
  reasoning?: string;
  result?: any;
  tokenUsage?: TokenUsage;
  startTime?: string;
  endTime?: string;
}

export interface QueryPayload {
  query: string;
  is_followup: boolean;
  parent_entry_id: string | null;
  include_project_files: boolean;
  additional_context: Record<string, any>;
  mode?: 'chat' | 'research'; // "chat" or "research" mode
}

export interface ChatMessageResponse {
  message_id: string;
  timestamp: string;
  status: 'saved';
}

export class StreamingAPIService {
  // Cache and rate limiting for failed queries to prevent infinite loops
  private static failedQueryCache = new Map<string, { result: StreamingQueryResponse; timestamp: number }>();
  private static recentRequests = new Map<string, number>();
  private static readonly CACHE_TTL = 60000; // 1 minute cache for failed queries
  private static readonly RATE_LIMIT_MS = 1000; // Maximum 1 request per second per query

  // Fetch the full result for a completed query
  async fetchQueryFullResult(queryId: string, initialQuery: string = ""): Promise<StreamingQueryResponse> {
    try {
      // Guard against undefined or empty queryId
      if (!queryId || queryId === 'undefined' || queryId.trim() === '') {
        console.error('fetchQueryFullResult called with invalid queryId:', queryId);
        throw new Error('Invalid queryId provided to fetchQueryFullResult');
      }

      // Rate limiting: prevent too frequent requests for the same query
      const lastRequest = StreamingAPIService.recentRequests.get(queryId);
      const now = Date.now();
      if (lastRequest && (now - lastRequest) < StreamingAPIService.RATE_LIMIT_MS) {
        console.log('Rate limiting query request:', queryId);
        await new Promise(resolve => setTimeout(resolve, StreamingAPIService.RATE_LIMIT_MS));
      }
      StreamingAPIService.recentRequests.set(queryId, now);

      // Check cache for failed queries to prevent infinite loops
      const cached = StreamingAPIService.failedQueryCache.get(queryId);
      if (cached && (now - cached.timestamp) < StreamingAPIService.CACHE_TTL) {
        console.log('Returning cached failed query result:', queryId);
        return cached.result;
      }

      const response = await fetch(API_URLS.getQueryFullResultUrl(queryId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        const parsedError = BackendErrorParser.parseError(new Error(errorText), response);
        throw new Error(JSON.stringify(parsedError));
      }

      const result = await response.json();

      // Cache failed queries to prevent infinite retry loops
      if (result.status === 'failed') {
        console.log('Caching failed query result to prevent infinite loops:', queryId);
        StreamingAPIService.failedQueryCache.set(queryId, { result, timestamp: now });
      }

      // Check for mock data scenarios (centralized)
      const query = initialQuery || result.query || '';
      const { MockDataService } = await import('./mockData');
      const scenario = MockDataService.findScenario(query);

      if (scenario) {
        console.log(`Using mock data for scenario: ${scenario.id}`);
        const mockResponse = await MockDataService.loadResponse(scenario.id);
        if (mockResponse) {
          return { ...result, ...mockResponse };
        }
      }

      return result;

    } catch (error) {
      console.error('Error fetching query full result:', error);
      throw error;
    }
  }

  // Submit query to project and get streaming response
  async submitProjectQuery(
    projectId: string,
    query: string,
    parentEntryId?: string,
    includeProjectFiles: boolean = true,
    additionalContext: Record<string, any> = {},
    agentMode?: boolean
  ): Promise<{
    queryId: string;
    entryId: string;
    eventSource: EventSource;
  }> {
    try {
      // Convert boolean agentMode to string mode: true = "research", false = "chat"
      const isAgentMode = agentMode ?? true;
      const mode: 'chat' | 'research' = isAgentMode ? 'research' : 'chat';
      console.log('[StreamingAPI.submitProjectQuery] agentMode param:', agentMode, '→ mode:', mode);

      // Prepare the query payload
      const queryPayload: QueryPayload = {
        query: query,
        is_followup: Boolean(parentEntryId),
        parent_entry_id: parentEntryId || null,
        include_project_files: includeProjectFiles,
        additional_context: additionalContext,
        mode: mode
      };

      console.log('[StreamingAPI.submitProjectQuery] Sending payload with mode:', queryPayload.mode);

      const response = await fetch(API_URLS.getProjectQueryUrl(projectId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(queryPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const parsedError = BackendErrorParser.parseError(new Error(errorText), response);
        throw new Error(JSON.stringify(parsedError));
      }

      const result = await response.json();
      const queryId = result.query_id;
      const entryId = result.entry_id;

      // Set up Server-Sent Events for streaming updates
      const eventSource = openQueryStream(queryId);

      // Log when the connection opens
      eventSource.onopen = () => {
        console.log('SSE connection opened for query:', queryId);
      };

      return { queryId, eventSource, entryId };
    } catch (error) {
      console.error('Error submitting project query:', error);
      throw error;
    }
  }

  // Submit query with multiple files to project
  async submitProjectQueryWithFiles(
    projectId: string,
    query: string,
    files: File[],
    parentEntryId?: string,
    includeProjectFiles: boolean = true,
    additionalContext: Record<string, any> = {},
    agentMode?: boolean,
    onUploadProgress?: (progress: number) => void
  ): Promise<{
    queryId: string;
    entryId: string;
    eventSource: EventSource;
    failedFiles?: string[];
  }> {
    let failedFiles: string[] = [];

    try {
      // Convert boolean agentMode to string mode: true = "research", false = "chat"
      const isAgentMode = agentMode ?? true;
      console.log('[StreamingAPI.submitProjectQueryWithFiles] agentMode param:', agentMode, '→ isAgentMode:', isAgentMode);

      // Upload and process all files
      if (files && files.length > 0) {
        failedFiles = await this.uploadProjectFiles(projectId, files, onUploadProgress);

        // Trigger file registry refresh in background (non-blocking)
        this.refreshFileRegistryInBackground();
      }

      // Then submit the query (continue even if some files failed)
      // Pass the boolean agentMode to submitProjectQuery which will convert it internally
      console.log('[StreamingAPI.submitProjectQueryWithFiles] Calling submitProjectQuery with isAgentMode:', isAgentMode);
      const result = await this.submitProjectQuery(
        projectId,
        query,
        parentEntryId,
        includeProjectFiles,
        additionalContext,
        isAgentMode
      );

      return {
        ...result,
        failedFiles: failedFiles.length > 0 ? failedFiles : undefined
      };
    } catch (error) {
      // Re-throw the error to be handled by the caller
      throw error;
    }
  }

  // Helper method to set up streaming for an existing query
  streamQueryStatus(queryId: string): EventSource {
    return openQueryStream(queryId);
  }



  // Process a single uploaded file
  async processFile(fileId: string, forceReprocess: boolean = false): Promise<void> {
    const response = await fetch(API_URLS.getFileProcessUrl(fileId, forceReprocess), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      // throw new Error(`Failed to process file: ${response.status} - ${errorText}`);
      console.error(`Failed to process file: ${response.status} - ${errorText}`);
    }

    // The response is a FileProcessingResult object
    const result = await response.json();

    // Check if processing was successful
    if (result.status === 'failed' && result.error_message) {
      // throw new Error(`File processing failed: ${result.error_message}`);
      console.error(`File processing failed: ${result.error_message}`);
    }

    console.log(`File ${fileId} processed successfully: ${result.chunk_count || 0} chunks, ${result.vector_count || 0} vectors`);
  }

  // Upload a single file with explicit scope
  async uploadFile(file: File, scope: 'project' | 'global', projectId?: string, processImmediately: boolean = false): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', scope);
    formData.append('process_immediately', processImmediately.toString());

    if (scope === 'project' && projectId) {
      formData.append('project_id', projectId);
    }

    const response = await fetch(API_URLS.FILE_UPLOAD_GLOBAL, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result.file_id;
  }

  // Upload files to project storage only (no duplication)
  // Returns array of failed file names (empty if all succeeded)
  async uploadProjectFiles(projectId: string, files: File[], onProgress?: (progress: number) => void, processImmediately: boolean = false): Promise<string[]> {
    const failedFiles: string[] = [];

    // If processing immediately, we only have 1 stage per file (upload+process combined)
    // Otherwise, we have 2 stages (upload, then process)
    const totalStages = processImmediately ? files.length : files.length * 2;
    let completedStages = 0;

    // Helper function to update progress
    const updateProgress = () => {
      completedStages++;
      if (onProgress) {
        const progress = Math.round((completedStages / totalStages) * 100);
        onProgress(progress);
      }
    };

    for (const file of files) {
      try {
        if (processImmediately) {
          // Single stage: Upload and process immediately (backend handles processing)
          await this.uploadFile(file, 'project', projectId, true);
          updateProgress();
        } else {
          // Stage 1: Upload to project scope only
          const fileId = await this.uploadFile(file, 'project', projectId, false);
          updateProgress();

          // Stage 2: Process the uploaded file
          await this.processFile(fileId);
          updateProgress();
        }
      } catch (error) {
        console.error(`Failed to upload/process file ${file.name}:`, error);
        failedFiles.push(file.name);
        // Still update progress for the failed stages (to keep progress consistent)
        const remainingStagesForThisFile = 2 - (completedStages % 2 || 2);
        completedStages += remainingStagesForThisFile;
        if (onProgress) {
          const progress = Math.round((completedStages / totalStages) * 100);
          onProgress(progress);
        }
        // Continue with next file
      }
    }

    return failedFiles;
  }

  // Refresh file registry in the background (non-blocking)
  private refreshFileRegistryInBackground(): void {
    // Use setTimeout to ensure this runs asynchronously and doesn't block
    setTimeout(async () => {
      try {
        console.log('Refreshing file registry in background...');
        await fileRegistryService.refreshFileRegistry();
        console.log('File registry refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh file registry in background:', error);
        // Don't throw - this is a background operation and shouldn't affect the main flow
      }
    }, 0);
  }


  // Submit sync query - re-run initial query with proper context
  // Uses the project query endpoint which returns immediately and streams updates via SSE
  async submitSyncQuery(
    projectId: string,
    query: string,
    projectName: string,
    uploadedFiles: any[] = []
  ): Promise<{
    queryId: string;
    entryId: string;
    eventSource: EventSource;
  }> {
    try {
      // Prepare the sync query payload using the project query endpoint format
      const syncPayload = {
        query: query,
        mode: 'research', // Full orchestration for sync
        is_followup: false,
        parent_entry_id: null,
        include_project_files: true,
        require_approval: false, // Skip approval step for sync queries
        additional_context: {
          is_sync: true,
          project_name: projectName,
          uploaded_files: uploadedFiles.map(f => ({
            file_id: f.file_id,
            filename: f.filename,
            file_type: f.file_type || 'document'
          }))
        }
      };

      console.log('[StreamingAPI.submitSyncQuery] Submitting sync to project endpoint:', projectId);

      // Use the project query endpoint which returns immediately with query_id
      const response = await fetch(API_URLS.getProjectQueryUrl(projectId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(syncPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[StreamingAPI.submitSyncQuery] Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const queryId = result.query_id || result.queryId;
      const entryId = result.entry_id || result.entryId || '';

      console.log('[StreamingAPI.submitSyncQuery] Sync started, queryId:', queryId, 'entryId:', entryId);

      // Set up Server-Sent Events for streaming updates
      const eventSource = openQueryStream(queryId);

      // Log when the connection opens
      eventSource.onopen = () => {
        console.log('[StreamingAPI.submitSyncQuery] SSE connection opened for sync query:', queryId);
      };

      return { queryId, eventSource, entryId };
    } catch (error) {
      console.error('Error submitting sync query:', error);
      throw error;
    }
  }

  // Submit sync query - re-run initial query with proper context
  async refreshProjectQuery(
    projectId: string,
    targetQueryId?: string,
  ): Promise<{
    queryId: string;
    entryId: string;
    eventSource: EventSource;
  }> {
    try {
      // Prepare the refresh request payload matching ProjectRefreshRequest
      const refreshPayload = {
        include_followups: true,
        use_cached_results: false
      };

      // Use the project refresh endpoint
      const response = await fetch(API_URLS.getProjectRefreshUrl(projectId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(refreshPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Refresh endpoint error:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const results = await response.json();

      // The refresh endpoint returns an array of results for each query
      if (!Array.isArray(results) || results.length === 0) {
        throw new Error('No queries to refresh in this project');
      }

      // Find the result matching the targetQueryId if provided, otherwise use the first result
      let firstResult;
      if (targetQueryId) {
        firstResult = results.find(r =>
          (r.queryId === targetQueryId) || (r.query_id === targetQueryId)
        );

        if (!firstResult) {
          console.warn(`Query ID ${targetQueryId} not found in refresh results, using first result`);
          firstResult = results[0];
        }
      } else {
        // Default to the first result (the initial query)
        firstResult = results[0];
      }

      const queryId = firstResult.queryId || firstResult.query_id;
      const entryId = firstResult.entry_id || '';

      // Set up Server-Sent Events for streaming updates
      const eventSource = openQueryStream(queryId);

      return { queryId, eventSource, entryId };
    } catch (error) {
      console.error('Error submitting sync query:', error);
      throw error;
    }
  }

  // Submit follow-up query (conversational, no streaming)
  async submitFollowupQuery(
    queryId: string,
    followupQuery: string,
    includeProjectFiles: boolean = true,
    additionalContext: Record<string, any> = {}
  ): Promise<{
    queryId: string;
    answer: string;
    status: string;
  }> {
    const response = await fetch(API_URLS.getQueryFollowupUrl(queryId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: followupQuery,
        include_project_files: includeProjectFiles,
        additional_context: additionalContext
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to submit follow-up query: ${response.status}`);
    }

    // Backend returns ChatResponse: { queryId, status, query, answer, executionTimeMs, timestamp }
    const result = await response.json();

    return {
      queryId: result.queryId || result.query_id,
      answer: result.answer,
      status: result.status
    };
  }

  // Submit follow-up query with multiple files (conversational, no streaming)
  async submitFollowupQueryWithFiles(
    projectId: string,
    queryId: string,
    followupQuery: string,
    files: File[],
    includeProjectFiles: boolean = true,
    additionalContext: Record<string, any> = {},
    onUploadProgress?: (progress: number) => void
  ): Promise<{
    queryId: string;
    answer: string;
    status: string;
    failedFiles?: string[];
  }> {
    let failedFiles: string[] = [];

    try {
      // Upload and process all files IMMEDIATELY before submitting the follow-up query
      // This ensures files are vectorized and searchable when the agent runs
      if (files && files.length > 0) {
        failedFiles = await this.uploadProjectFiles(projectId, files, onUploadProgress, true);

        // Trigger file registry refresh in background (non-blocking)
        this.refreshFileRegistryInBackground();
      }

      // Submit the follow-up query and get the response (continue even if some files failed)
      const response = await fetch(API_URLS.getQueryFollowupUrl(queryId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: followupQuery,
          include_project_files: includeProjectFiles,
          additional_context: additionalContext
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit follow-up query: ${response.status}`);
      }

      // Backend returns ChatResponse: { queryId, status, query, answer, executionTimeMs, timestamp }
      const result = await response.json();

      return {
        queryId: result.queryId || result.query_id,
        answer: result.answer,
        status: result.status,
        failedFiles: failedFiles.length > 0 ? failedFiles : undefined
      };
    } catch (error) {
      // Re-throw the error to be handled by the caller
      throw error;
    }
  }

  // Submit user feedback for pending approval (uses approve endpoint)
  async submitApprovalFeedback(queryId: string, userMessage: string): Promise<void> {
    const response = await fetch(API_URLS.getQueryApproveUrl(queryId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to submit approval: ${response.status} - ${errorText}`);
    }
  }

  // Add a chat message to a conversation entry
  async addChatMessage(
    projectId: string,
    request: ChatMessageRequest
  ): Promise<ChatMessageResponse> {
    const url = API_URLS.getProjectChatMessagesUrl(projectId);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add chat message: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

}

export const streamingAPI = new StreamingAPIService();